import React, { useCallback, useRef, useState, useMemo } from 'react';
import { Table, Tooltip, Card, Statistic, Tag, Row, Col, DatePicker, Alert, Button, message } from 'antd';
import { CalendarOutlined } from '@ant-design/icons';
import FlexFileInput from '../../common/FlexFileInput';
import { readXlsxToJson } from '../../../utils/xlsx';
import { convertJsonData } from '../../../utils/data';
import { getColumnSearchProps } from '../../common/TableFilter';
import { useGlobalContext } from '../../../App';
import moment from 'moment';

/**
 * 導入散單數據（文件2）
 * 支持日期選擇：可指定服務週，也可從Excel中自動讀取日期
 */
export default () => {
    const { scatteredData, setScatteredData } = useGlobalContext();
    const [searchText, setSearchText] = useState('');
    const [searchedColumn, setSearchedColumn] = useState('');
    const searchInput = useRef(null);
    const [loading, setLoading] = useState(false);
    const [selectedWeek, setSelectedWeek] = useState(null);

    // 統計日期分佈
    const dateDistribution = useMemo(() => {
        if (!scatteredData || scatteredData.length === 0) return { dates: [], missingCount: 0, totalWithDate: 0 };
        const dateCounts = {};
        let missingCount = 0;
        scatteredData.forEach(o => {
            const d = o.service_date;
            if (d && d.trim()) {
                dateCounts[d] = (dateCounts[d] || 0) + 1;
            } else {
                missingCount++;
            }
        });
        const dates = Object.entries(dateCounts).sort((a, b) => a[0].localeCompare(b[0]));
        return { dates, missingCount, totalWithDate: scatteredData.length - missingCount };
    }, [scatteredData]);

    const columns = [
        {
            title: '服務日期', dataIndex: 'service_date', key: 'service_date', width: 120,
            render: (text) => text ? <Tag color="blue">{text}</Tag> : <Tag color="red">未指定</Tag>,
            ...getColumnSearchProps('service_date', '服務日期', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        {
            title: '服務時間', dataIndex: 'service_time', key: 'service_time', width: 120,
        },
        {
            title: '客戶編號', dataIndex: 'customer_ID', key: 'customer_ID', width: 120, ellipsis: { showTitle: false },
            ...getColumnSearchProps('customer_ID', '客戶編號', searchInput, setSearchText, searchText, setSearchedColumn, searchedColumn),
        },
        {
            title: '客戶名稱', dataIndex: 'name', key: 'name', width: 150, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '集團公司編號', dataIndex: 'company_ID', key: 'company_ID', width: 120, },
        { title: '地址', dataIndex: 'address', key: 'address', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        {
            title: '服務種類', dataIndex: 'type', key: 'type', width: 100,
            render: (type) => {
                const color = type === 'PC' ? 'blue' : type === 'HY' ? 'green' : 'default';
                return <Tag color={color}>{type}</Tag>;
            },
            filters: [
                { text: 'PC', value: 'PC' },
                { text: 'HY', value: 'HY' },
            ],
            onFilter: (value, record) => record.type === value,
        },
        {
            title: '技術員', dataIndex: 'staff', key: 'staff', width: 150, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
        { title: '黑名單工作人員', dataIndex: 'blacklist', key: 'blacklist', width: 160, ellipsis: { showTitle: false },
            render: (text) => <Tooltip placement="topLeft" title={text}>{text}</Tooltip>,
        },
    ];

    const handleImport = useCallback((e) => {
        setLoading(true);
        const files = e.target.files;
        if (files) {
            const f = files[0];
            readXlsxToJson(f, (result) => {
                setLoading(false);
                const converted = convertJsonData(result, columns);
                setScatteredData(converted);
            }, { headerRow: 1 });
        }
    }, []);

    // 選擇服務週後，為沒有日期的散單填充該週的日期
    const handleWeekChange = useCallback((date, dateString) => {
        setSelectedWeek(date);
        if (!date || !scatteredData || scatteredData.length === 0) return;

        // 計算該週一至週日的日期
        const weekStart = date.clone().startOf('isoWeek'); // 週一
        const weekDates = [];
        for (let i = 0; i < 7; i++) {
            weekDates.push(weekStart.clone().add(i, 'days').format('YYYY/MM/DD'));
        }

        // 為缺少日期的散單填充日期（輪流分配到週一至週日）
        const updated = scatteredData.map((order, idx) => {
            if (!order.service_date || !order.service_date.trim()) {
                return { ...order, service_date: weekDates[idx % 7] };
            }
            return order;
        });

        setScatteredData(updated);
        message.success(`已為 ${dateDistribution.missingCount} 張無日期散單填充 ${weekDates[0]} ~ ${weekDates[6]} 的日期`);
    }, [scatteredData, dateDistribution.missingCount]);

    // 快捷操作：將所有散單的日期設為同一天
    const handleSetAllDate = useCallback((date) => {
        if (!date || !scatteredData || scatteredData.length === 0) return;
        const dateStr = date.format('YYYY/MM/DD');
        const updated = scatteredData.map(order => ({ ...order, service_date: dateStr }));
        setScatteredData(updated);
        message.success(`已將所有 ${scatteredData.length} 張散單日期設為 ${dateStr}`);
    }, [scatteredData]);

    const pcCount = scatteredData?.filter(o => o.type === 'PC').length || 0;
    const hyCount = scatteredData?.filter(o => o.type === 'HY').length || 0;

    return (
        <div>
            <Card size="small" style={{ marginBottom: 16 }}>
                <FlexFileInput text="導入散單數據（文件2）" accept=".xlsx,.xls" onChange={handleImport} />
            </Card>

            {scatteredData && scatteredData.length > 0 && (
                <>
                    {/* 日期選擇區 */}
                    <Card size="small" title={<span><CalendarOutlined style={{ marginRight: 8 }} />日期設置</span>} style={{ marginBottom: 16 }}>
                        {dateDistribution.missingCount > 0 && (
                            <Alert
                                message={`有 ${dateDistribution.missingCount} 張散單缺少服務日期，請選擇服務週或指定日期`}
                                type="warning"
                                showIcon
                                style={{ marginBottom: 12 }}
                            />
                        )}
                        <Row gutter={16} align="middle">
                            <Col>
                                <span style={{ marginRight: 8 }}>選擇服務週：</span>
                                <DatePicker
                                    picker="week"
                                    value={selectedWeek}
                                    onChange={handleWeekChange}
                                    placeholder="選擇週"
                                    style={{ width: 200 }}
                                    format="YYYY年第w週"
                                />
                            </Col>
                            <Col>
                                <span style={{ marginRight: 8 }}>指定統一日期：</span>
                                <DatePicker
                                    onChange={handleSetAllDate}
                                    placeholder="所有散單同一天"
                                    style={{ width: 200 }}
                                    format="YYYY/MM/DD"
                                />
                            </Col>
                        </Row>
                    </Card>

                    {/* 日期分佈統計 */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Row gutter={16}>
                            <Col span={6}><Statistic title="散單總數" value={scatteredData.length} /></Col>
                            <Col span={6}><Statistic title="PC散單" value={pcCount} valueStyle={{ color: '#1890ff' }} /></Col>
                            <Col span={6}><Statistic title="HY散單" value={hyCount} valueStyle={{ color: '#52c41a' }} /></Col>
                            <Col span={6}><Statistic
                                title="缺日期散單"
                                value={dateDistribution.missingCount}
                                valueStyle={{ color: dateDistribution.missingCount > 0 ? '#ff4d4f' : '#52c41a' }}
                            /></Col>
                        </Row>
                        {dateDistribution.dates.length > 0 && (
                            <div style={{ marginTop: 12, padding: '8px 12px', background: '#fafafa', borderRadius: 4 }}>
                                <span style={{ color: '#666', marginRight: 8 }}>日期分佈：</span>
                                {dateDistribution.dates.map(([date, count]) => (
                                    <Tag key={date} color="blue" style={{ marginBottom: 4 }}>
                                        {date}: {count}張
                                    </Tag>
                                ))}
                            </div>
                        )}
                    </Card>
                </>
            )}

            <div className="table-container">
                <Table
                    bordered
                    scroll={{ x: 'calc(800px + 50%)', y: 500 }}
                    loading={loading}
                    columns={columns}
                    dataSource={scatteredData || []}
                    size="small"
                />
            </div>
        </div>
    );
};
